import { useEffect, useState, useCallback } from 'react';

declare global {
  interface Window {
    silktideCookieBannerManager?: {
      updateCookieBannerConfig: (config: any) => void;
      destroyCookieBanner?: () => void;
    };
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

// Check if the user has already made a cookie consent choice
const checkExistingConsent = (): boolean => {
  // Silktide stores consent in a cookie named "silktide_cookie_consent" or similar
  const cookies = document.cookie.split(';').map(c => c.trim());
  const hasConsentCookie = cookies.some(c => 
    c.startsWith('silktide') || c.startsWith('cookie_consent')
  );
  if (hasConsentCookie) return true;

  // Also check localStorage as a fallback
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.toLowerCase().includes('silktide') || key.toLowerCase().includes('cookie_consent'))) {
        return true;
      }
    }
  } catch {
    // localStorage not available
  }

  return false;
};

export const useCookieConsent = () => {
  const hostname = window.location.hostname;
  const isProduction = hostname !== 'localhost' && 
                        hostname !== '127.0.0.1' &&
                        !hostname.endsWith('.lovable.app');

  // In non-production, always treat as consented so login isn't blocked
  const [hasConsented, setHasConsented] = useState(!isProduction || checkExistingConsent());

  const markConsented = useCallback(() => {
    setHasConsented(true);
  }, []);

  useEffect(() => {
    if (!isProduction) {
      return;
    }

    if (checkExistingConsent()) {
      setHasConsented(true);
    }

    let cssLink: HTMLLinkElement | null = null;
    let script: HTMLScriptElement | null = null;

    // Defer loading to avoid blocking initial render
    const deferredId = 'requestIdleCallback' in window
      ? (window as any).requestIdleCallback(load)
      : setTimeout(load, 1000);

    function load() {
      cssLink = document.createElement('link');
      cssLink.id = 'silktide-consent-manager-css';
      cssLink.rel = 'stylesheet';
      cssLink.href = '/silktide/silktide-consent-manager.css';
      document.head.appendChild(cssLink);

      script = document.createElement('script');
      script.src = '/silktide/silktide-consent-manager.js';
      script.async = true;

      script.onload = () => {
        if (window.silktideCookieBannerManager) {
          window.silktideCookieBannerManager.updateCookieBannerConfig({
            background: { showBackground: true },
            cookieIcon: { position: "bottomRight" },
            cookieTypes: [
              {
                id: "necessary",
                name: "Necessary",
                description: "<p>These cookies are necessary for the website to function properly and cannot be switched off. They help with things like logging in and setting your privacy preferences.</p>",
                required: true,
                onAccept: function() { console.log('Necessary cookies accepted'); markConsented(); }
              },
              {
                id: "analytics",
                name: "Analytics",
                description: "<p>These cookies help us improve the site by tracking which pages are most popular and how visitors move around the site.</p>",
                defaultValue: true,
                onAccept: function() {
                  markConsented();
                  if (window.gtag) {
                    window.gtag('consent', 'update', { analytics_storage: 'granted' });
                    if (window.dataLayer) { window.dataLayer.push({ 'event': 'consent_accepted_analytics' }); }
                  }
                },
                onReject: function() {
                  markConsented();
                  if (window.gtag) { window.gtag('consent', 'update', { analytics_storage: 'denied' }); }
                }
              },
              {
                id: "advertising",
                name: "Advertising",
                description: "<p>These cookies provide extra features and personalization to improve your experience. They may be set by us or by partners whose services we use.</p>",
                onAccept: function() {
                  markConsented();
                  if (window.gtag) {
                    window.gtag('consent', 'update', { ad_storage: 'granted', ad_user_data: 'granted', ad_personalization: 'granted' });
                    if (window.dataLayer) { window.dataLayer.push({ 'event': 'consent_accepted_advertising' }); }
                  }
                },
                onReject: function() {
                  markConsented();
                  if (window.gtag) { window.gtag('consent', 'update', { ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied' }); }
                }
              }
            ],
            text: {
              banner: {
                title: "We use cookies",
                description: "We use cookies to help us understand our website traffic and customize your experience. By clicking Accept, you agree to our use of cookies.",
                acceptButton: "Accept All",
                rejectButton: "Reject Non-Essential",
                manageCookiesButton: "Customize"
              },
              links: [{ text: "Privacy Policy", href: "/privacy" }]
            },
            closeModal: { acceptAllButton: "Accept All", rejectAllButton: "Reject All", saveButton: "Save Preferences" },
            layout: { position: "bottomCenter" },
            onAcceptAll: () => markConsented(),
            onRejectAll: () => markConsented(),
            onSavePreferences: () => markConsented(),
          });
        }
      };

      document.head.appendChild(script);
    }

    return () => {
      if ('requestIdleCallback' in window) {
        (window as any).cancelIdleCallback(deferredId);
      } else {
        clearTimeout(deferredId);
      }

      if (window.silktideCookieBannerManager?.destroyCookieBanner) {
        try { window.silktideCookieBannerManager.destroyCookieBanner(); } catch {}
      }
      cssLink?.remove();
      script?.remove();
      purgeSilktideDom();
      setTimeout(purgeSilktideDom, 500);
      setTimeout(purgeSilktideDom, 1500);
    };
  }, [isProduction, markConsented]);

  return { hasConsented };
};

/**
 * Aggressively remove every Silktide-injected DOM node (banner, modal,
 * backdrop, cookie icon, free-tier watermark wrapper, stylesheet, script).
 * Safe to call from any route — it only touches Silktide-owned nodes.
 */
export function purgeSilktideDom() {
  if (typeof document === 'undefined') return;
  const selectors = [
    '[id^="silktide"]',
    '[class*="silktide-"]',
    '[class^="st-button"]',
    '[class*=" st-button"]',
    'link#silktide-consent-manager-css',
    'script[src*="silktide-consent-manager.js"]',
  ];
  selectors.forEach((sel) => {
    document.querySelectorAll(sel).forEach((el) => el.remove());
  });
}
